/**
 * fetchConfiguration — fetch remote config from the API.
 *
 * Uses native fetch() — NOT Axios. Axios interceptors are not configured
 * at bootstrap time, so we use the browser-native API here.
 *
 * Returns null on every failure mode:
 *   - network error / timeout
 *   - non-200 HTTP status
 *   - invalid JSON body
 *   - Zod parse failure
 */

import { RemoteConfigurationSchema } from './types';
import type { RemoteConfiguration } from './types';

const CONFIG_FETCH_TIMEOUT_MS = 3000;

/**
 * Fetch the remote configuration from `{origin}/configuration.json`.
 *
 * Returns the parsed RemoteConfiguration on success, or null on any failure.
 */
export async function fetchConfiguration(origin: string): Promise<RemoteConfiguration | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, CONFIG_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${origin}/configuration.json`, {
      signal: controller.signal,
    });

    if (!response.ok) {
      return null;
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      return null;
    }

    const parsed = RemoteConfigurationSchema.safeParse(json);
    if (!parsed.success) {
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
